import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Bell,
  LogOut,
  User as UserIcon,
  UserRoundPlus,
  Trophy,
  Check,
  Maximize,
  Moon,
  Building2,
  Zap,
  PiggyBank,
  Target,
  Flame,
  MapPin,
  Globe,
  Fuel,
  Star,
  Users,
  Activity,
  DollarSign,
  ChevronDown,
  Search,
  AlertTriangle,
  // #1225 slice 7 — Settings (gear) icon for the super-admin Commands
  // page. Shown left of the Bell, gated to super_admin only.
  Settings as SettingsIcon,
  // #1225 round-2 — icons for the Settings dropdown items: Commands +
  // Queue Manager.
  Terminal as TerminalIcon,
  LayoutDashboard,
  // Switch User (impersonation) menu item icon + the header "Exit
  // impersonation" control icon.
  UserCog,
  // Create User (Settings → Create User) menu item icon.
  UserPlus,
  // project-waypoint#1396 follow-up — Support Tickets notification
  // section header icon (matches /support page branding).
  LifeBuoy,
  ArrowLeftRight,
} from 'lucide-react';
import {
  hasImpersonatorSession,
  getImpersonatorSession,
  exitImpersonation,
} from '../../../lib/impersonation';
import { useAuthStore } from '../../../stores/auth-store';
// ATL-001145 (SEC-036): Sign Out has to reach the server. The local
// store's `logout()` only clears this browser; `useLogout` is the
// mutation that calls POST /auth/logout, which is what revokes the
// refresh token AND bumps the user's tokenVersion so the access token
// (24h then, 15m since ATL-001144) stops being accepted.
import { useLogout } from '../../hooks/use-auth';
import { useProfileImage } from '../../../hooks/use-profile-image';
import { useOrganizations } from '../../../hooks/use-enterprise-features';
import { useSwitchOrg } from '../../../hooks/use-switch-org';
import { useMyMemberships } from '../../../hooks/use-auth';
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../../../hooks/use-notifications';
import { useDesktopNotifications } from '../../../hooks/use-desktop-notifications';
import { useMyAchievements } from '../../../hooks/use-badges';
import { useFeature } from '../../../hooks/use-enterprise-features';
import { resolveNotificationRoute } from '../../../pages/notifications/entity-routes';
import { CreateUserModal } from './CreateUserModal';
import tracxLogoLight from '../../../assets/tracx-logo-light.webp';

const BADGE_ICONS: Record<string, typeof Zap> = {
  'zap': Zap,
  'piggy-bank': PiggyBank,
  'target': Target,
  'flame': Flame,
  'map-pin': MapPin,
  'trophy': Trophy,
  'moon': Moon,
  'globe': Globe,
  'fuel': Fuel,
  'star': Star,
  'users': Users,
  'activity': Activity,
  'dollar-sign': DollarSign,
};

// ---------- Org Switcher ----------

function OrgSwitcher() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const currentOrgName = user?.organization?.name ?? user?.organizationName;
  const isSuperAdmin = user?.role === 'super_admin';

  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmOrg, setConfirmOrg] = useState<{ id: string; name: string } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Super-admin: list every org. Multi-org regular user: only their memberships.
  const { data: allOrganizations } = useOrganizations();
  const { data: memberships } = useMyMemberships();
  const organizations = isSuperAdmin
    ? allOrganizations
    : (memberships ?? []).map((m) => ({
        id: m.organizationId,
        name: m.organizationName,
        slug: m.organizationSlug,
      }));
  // Regular users only see the dropdown when they have ≥2 active memberships.
  const showDropdownUI = isSuperAdmin || (memberships?.length ?? 0) > 1;
  const switchOrg = useSwitchOrg();

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setSearchQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  // Auto-focus search when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [showDropdown]);

  if (!currentOrgName) return null;

  const filteredOrgs = (organizations ?? []).filter((org) =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleOrgClick = (org: { id: string; name: string }) => {
    if (org.id === user?.organizationId) {
      setShowDropdown(false);
      setSearchQuery('');
      return;
    }
    // Show confirmation popup
    setShowDropdown(false);
    setSearchQuery('');
    setConfirmOrg(org);
  };

  const handleConfirmSwitch = () => {
    if (!confirmOrg) return;

    // Show toast
    const toast = document.createElement('div');
    toast.style.cssText =
      'position:fixed;top:16px;right:16px;z-index:9999;padding:12px 20px;' +
      'border-radius:6px;background:#468AD4;color:#fff;font-size:13px;font-weight:500;' +
      'box-shadow:0 4px 12px rgba(0,0,0,0.2);display:flex;align-items:center;gap:8px;' +
      'animation:slideIn .25s ease-out;font-family:inherit;';
    // Static spinner SVG only (no user data) via innerHTML; the label — which
    // interpolates the org name — is set with textContent so the name can never
    // inject markup, and the copy is translated in the recipient's language.
    toast.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ' +
      'style="animation:spin 1s linear infinite"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>';
    const label = document.createElement('span');
    label.textContent = t('components.header.switchingTo', { name: confirmOrg.name });
    toast.appendChild(label);
    const style = document.createElement('style');
    style.textContent =
      '@keyframes slideIn{from{opacity:0;transform:translateX(40px)}to{opacity:1;transform:translateX(0)}}' +
      '@keyframes spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
    document.body.appendChild(toast);

    switchOrg.mutate(confirmOrg.id);
    setConfirmOrg(null);
  };

  // Single-membership non-super-admin: static label only.
  if (!showDropdownUI) {
    return (
      <div className="relative">
        <div
          className="flex items-center gap-1.5"
          style={{
            background: 'transparent',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            color: 'rgba(255,255,255,0.85)',
            fontSize: '12.5px',
            fontWeight: 500,
          }}
        >
          <Building2 className="h-3.5 w-3.5" style={{ opacity: 0.7 }} />
          <span className="max-w-[200px] truncate">{currentOrgName}</span>
        </div>
      </div>
    );
  }

  // Multi-org user (regular or super admin): searchable dropdown + confirmation
  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          data-testid="org-switcher-trigger"
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center gap-1.5"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background 0.15s',
            color: 'rgba(255,255,255,0.85)',
            fontSize: '12.5px',
            fontWeight: 500,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <Building2 className="h-3.5 w-3.5" style={{ opacity: 0.7 }} />
          <span className="max-w-[200px] truncate">{currentOrgName}</span>
          <ChevronDown className="h-3 w-3" style={{ opacity: 0.5 }} />
        </button>

        {showDropdown && (
          <div
            className="absolute right-0 mt-1 z-40"
            style={{
              borderRadius: '6px',
              boxShadow: 'var(--vz-box-shadow-lg)',
              top: '100%',
              backgroundColor: 'var(--vz-card-bg)',
              minWidth: '280px',
              maxHeight: '380px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Search input */}
            <div style={{ padding: '8px', borderBottom: '1px solid var(--vz-border-color)' }}>
              <div style={{ position: 'relative' }}>
                <Search
                  style={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '14px',
                    height: '14px',
                    color: '#878a99',
                  }}
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('components.header.searchOrganizations')}
                  style={{
                    width: '100%',
                    padding: '6px 10px 6px 28px',
                    fontSize: '12.5px',
                    borderRadius: '4px',
                    border: '1px solid var(--vz-input-border)',
                    backgroundColor: 'var(--vz-input-bg)',
                    color: 'var(--vz-input-color)',
                    outline: 'none',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#468AD4'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = ''; }}
                />
              </div>
            </div>

            {/* Org list */}
            <div style={{ overflowY: 'auto', maxHeight: '300px', padding: '4px 0' }}>
              {filteredOrgs.map((org) => {
                const isActive = org.id === user?.organizationId;
                return (
                  <button
                    key={org.id}
                    data-testid="org-switcher-option"
                    onClick={() => handleOrgClick(org)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors duration-150"
                    style={{
                      fontSize: '13px',
                      color: isActive ? '#468AD4' : 'var(--vz-body-color)',
                      fontWeight: isActive ? 600 : 400,
                      backgroundColor: isActive ? 'var(--vz-primary-bg-subtle)' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = 'var(--vz-body-bg)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = isActive ? 'var(--vz-primary-bg-subtle)' : 'transparent';
                    }}
                  >
                    <Building2 className="h-3.5 w-3.5 flex-shrink-0" style={{ opacity: 0.5 }} />
                    <span className="truncate">{org.name}</span>
                    {isActive && <Check className="h-3.5 w-3.5 ml-auto flex-shrink-0" />}
                  </button>
                );
              })}
              {filteredOrgs.length === 0 && (
                <div className="px-3 py-4 text-center" style={{ fontSize: '12px', color: '#878a99' }}>
                  {searchQuery ? t('components.header.noMatchingOrganizations') : t('components.header.noOrganizations')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation popup */}
      {confirmOrg && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(2px)',
          }}
          onClick={() => setConfirmOrg(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--vz-card-bg)',
              borderRadius: '8px',
              padding: '24px',
              width: '380px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: 'rgba(247, 184, 75, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <AlertTriangle style={{ width: 18, height: 18, color: '#f7b84b' }} />
              </div>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--vz-heading-color)', margin: 0 }}>
                {t('components.header.switchOrganization')}
              </h3>
            </div>

            <p style={{ fontSize: '13px', color: 'var(--vz-body-color)', marginBottom: '20px', lineHeight: 1.5 }}>
              <Trans
                i18nKey="components.header.confirmSwitch"
                values={{ name: confirmOrg.name }}
                components={{ strong: <strong /> }}
              />
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={() => setConfirmOrg(null)}
                style={{
                  padding: '7px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '4px',
                  border: '1px solid var(--vz-border-color)',
                  backgroundColor: 'var(--vz-card-bg)',
                  color: 'var(--vz-body-color)',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmSwitch}
                style={{
                  padding: '7px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: '#468AD4',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t('components.header.yesSwitch')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function Header() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const { mutate: signOut } = useLogout();
  const isSuperAdmin = user?.role === 'super_admin';
  const { profileImage } = useProfileImage();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  // #1225 round-1 — Settings (gear) icon dropdown. Two items: Commands +
  // Queue Manager. Both open in a new tab.
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  // Create User (Settings → Create User) modal. Opened from the Settings
  // dropdown; renders the CreateUserModal at the end of the header tree.
  const [showCreateUser, setShowCreateUser] = useState(false);

  // Switch User (admin impersonation). When an app-level impersonation is
  // active, the operator's own session is stashed in sessionStorage and
  // the header shows an "Exit" control to restore it — independent of the
  // (impersonated) user's role, so it survives the role-gated header.
  const impersonatorSession = hasImpersonatorSession() ? getImpersonatorSession() : null;

  const notifRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);

  const { data: unreadCount } = useUnreadNotificationCount();
  const { data: notificationsData } = useNotifications({ limit: 10 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  // #1535 — gate the "View all notifications" footer link + the
  // bell-click navigation behind the same enterprise feature that
  // gates the /notifications route render. When the feature is OFF the
  // bell stays as the only surface; with no quick-glance loss.
  const notificationsCenterOn = useFeature('notifications_center');

  // Real-time bell refresh + OS desktop notifications, driven by
  // the server's `notification:new` WebSocket event. Mounted here because
  // the Header is always present in the authenticated app shell.
  useDesktopNotifications();

  const notifications = notificationsData?.data ?? [];

  // Split real notifications into "Support Tickets" (anything from the
  // support module — comments, status / priority / assignee changes)
  // and "Other". Support ones get the platinum-style card to match the
  // design above. Backend already sorts by createdAt desc so the
  // .filter() preserves that ordering.
  // (project-waypoint#1396 follow-up)
  const supportNotifs = notifications.filter((n) =>
    typeof n.type === 'string' && n.type.startsWith('support_ticket_'),
  );
  const otherNotifs = notifications.filter(
    (n) => !(typeof n.type === 'string' && n.type.startsWith('support_ticket_')),
  );
  const supportUnread = supportNotifs.filter((n) => !n.read).length;

  // ---- Highest Earned Badge ----
  const { data: achievements } = useMyAchievements();
  const highestBadge = useMemo(() => {
    if (!achievements || achievements.length === 0) return null;
    // Pick the badge with the highest metricValue; ties broken by most recent earnedAt
    return [...achievements].sort((a, b) => {
      const valDiff = (b.metricValue ?? 0) - (a.metricValue ?? 0);
      if (valDiff !== 0) return valDiff;
      return new Date(b.earnedAt).getTime() - new Date(a.earnedAt).getTime();
    })[0];
  }, [achievements]);

  const totalBadgeCount = unreadCount ?? 0;

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettingsMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = () => {
    // ATL-001145 (SEC-036): this used to call the auth store's `logout()`
    // directly, which just cleared localStorage — the server was never told,
    // so the refresh token stayed live and the (then 24h) access token kept working.
    // `useLogout` posts to /auth/logout (revoking both) and clears the store in
    // its onSuccess; it swallows API failures, so the local sign-out and the
    // redirect still happen if the request cannot be made.
    signOut(undefined, {
      onSettled: () => navigate('/login', { replace: true }),
    });
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  // Derive initials safely — works on any device without localStorage dependency
  const userInitials = user
    ? `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase() || 'U'
    : 'U';

  const iconBtnStyle: React.CSSProperties = {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    color: 'rgba(255,255,255,0.85)',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    transition: 'background 0.15s',
  };

  return (
    <>
    <header
      className="fixed top-0 left-0 right-0 z-[1002] flex items-center justify-between"
      style={{
        height: '50px',
        backgroundColor: '#0D1E33',
        padding: '0 16px',
      }}
    >
      {/* LEFT: Logo */}
      <div className="flex items-center" style={{ height: '50px' }}>
        <img
          src={tracxLogoLight}
          alt="Tracx"
          style={{
            height: '24px',
            width: 'auto',
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))',
          }}
        />
      </div>

      {/* RIGHT: User avatar + name + icon buttons */}
      <div className="flex flex-row-reverse items-center gap-3">
        {/* User avatar + name */}
        <div ref={userMenuRef} className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {/* TODO: profileImage is localStorage-only; falls back to initials for cross-device support */}
            {profileImage ? (
              <img
                src={profileImage}
                alt={t('nav.profile')}
                className="rounded-full"
                style={{
                  width: '30px',
                  height: '30px',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div
                className="flex items-center justify-center rounded-full"
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                {userInitials}
              </div>
            )}
            <span
              style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.85)',
                fontWeight: 500,
              }}
            >
              {user?.firstName} {user?.lastName}
            </span>
            {highestBadge && (() => {
              const BadgeIcon = BADGE_ICONS[highestBadge.badge.icon] || Trophy;
              return (
                <span title={highestBadge.badge.name} style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <BadgeIcon style={{ width: 16, height: 16, color: '#D4A843' }} />
                </span>
              );
            })()}
          </button>

          {showUserMenu && (
            <div
              className="absolute right-0 mt-1 w-48 z-40"
              style={{
                borderRadius: '4.8px',
                boxShadow: 'var(--vz-box-shadow-lg)',
                padding: '8px 0',
                top: '100%',
                backgroundColor: 'var(--vz-card-bg)',
              }}
            >
              <div
                className="px-4 py-2"
                style={{ borderBottom: '1px solid var(--vz-border-color)' }}
              >
                <p
                  style={{
                    fontSize: '13.2px',
                    fontWeight: 500,
                    color: 'var(--vz-body-color)',
                  }}
                >
                  {user?.firstName} {user?.lastName}
                </p>
                <p style={{ fontSize: '12px', color: '#7A8A9A' }}>
                  {user?.email}
                </p>
              </div>

              <button
                className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors duration-150"
                style={{ fontSize: '13.2px', color: 'var(--vz-heading-color)' }}
                onClick={() => {
                  navigate('/profile');
                  setShowUserMenu(false);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--vz-body-bg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <UserIcon className="h-4 w-4" />
                {t('nav.profile')}
              </button>

              <button
                onClick={() => {
                  navigate('/badges');
                  setShowUserMenu(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors duration-150"
                style={{ fontSize: '13.2px', color: 'var(--vz-heading-color)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--vz-body-bg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Trophy className="h-4 w-4" />
                {t('components.header.badges')}
              </button>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors duration-150"
                style={{
                  fontSize: '13.2px',
                  color: '#ed5e5e',
                  borderTop: '1px solid var(--vz-border-color)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--vz-danger-bg-subtle)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <LogOut className="h-4 w-4" />
                {t('components.header.logout')}
              </button>
            </div>
          )}
        </div>

        {/* Divider */}
        <div
          style={{
            width: '1px',
            height: '24px',
            backgroundColor: 'rgba(255,255,255,0.15)',
          }}
        />

        {/* Org Switcher */}
        <OrgSwitcher />

        {/* Tracx admin pill — super_admin only */}
        {isSuperAdmin && (
          <button
            onClick={() => navigate('/admin/dashboard')}
            title={t('components.header.openTracxAdmin')}
            style={{
              backgroundColor: 'rgba(70, 138, 212, 0.15)',
              border: '1px solid rgba(70, 138, 212, 0.4)',
              color: '#9ec5ee',
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              padding: '4px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginLeft: '8px',
            }}
          >
            {t('components.header.admin')}
          </button>
        )}

        {/* Divider */}
        <div
          style={{
            width: '1px',
            height: '24px',
            backgroundColor: 'rgba(255,255,255,0.15)',
          }}
        />

        {/* #1225 round-1 — Settings (gear) dropdown. Super-admin only.
            Two child items: Commands (Scheduled Commands page) + Queue
            Manager (Bull Board). Both open in a NEW TAB so the operator
            can keep the current page open. */}
        {isSuperAdmin && (
          <div ref={settingsRef} className="relative">
            <button
              data-testid="header-settings-button"
              aria-label={t('components.header.adminSettings')}
              aria-haspopup="menu"
              aria-expanded={showSettingsMenu}
              onClick={() => setShowSettingsMenu((v) => !v)}
              style={iconBtnStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <SettingsIcon className="h-[18px] w-[18px]" />
            </button>

            {showSettingsMenu && (
              <div
                role="menu"
                className="absolute right-0 mt-1 z-40"
                style={{
                  minWidth: 260,
                  borderRadius: '6px',
                  boxShadow: 'var(--vz-box-shadow-lg)',
                  padding: '6px 0',
                  top: '100%',
                  backgroundColor: 'var(--vz-card-bg)',
                  border: '1px solid var(--vz-border-color, rgba(0,0,0,0.08))',
                }}
              >
                <a
                  data-testid="header-settings-menu-commands"
                  href="/admin/commands"
                  target="_blank"
                  rel="noopener noreferrer"
                  role="menuitem"
                  onClick={() => setShowSettingsMenu(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '10px 14px',
                    color: 'var(--vz-body-color, #1f2937)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--vz-light, #f3f4f6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <TerminalIcon
                    className="shrink-0"
                    style={{
                      width: 18,
                      height: 18,
                      marginTop: 2,
                      color: 'var(--vz-primary, #468AD4)',
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{t('components.header.commands')}</span>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--vz-body-secondary-color, #6b7280)',
                      }}
                    >
                      {t('components.header.commandsDesc')}
                    </span>
                  </div>
                </a>
                <a
                  data-testid="header-settings-menu-queues"
                  // #1225 round-3 — Bull Board is mounted on Express,
                  // not on React Router. Browser GETs don't carry the
                  // Bearer header, so we open the API-side bootstrap
                  // endpoint which verifies the JWT, writes an HttpOnly
                  // cookie scoped to /admin/queues, and redirects to
                  // the dashboard. We target the API origin directly
                  // (rather than the SPA origin) so we don't depend on
                  // a Vite dev-server proxy — in prod, API + SPA share
                  // the origin, so the URL collapses to a plain path.
                  href={(() => {
                    const apiBase =
                      (import.meta as unknown as Record<string, Record<string, string>>).env
                        ?.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
                    const apiOrigin = apiBase.replace(/\/api\/v1\/?$/, '');
                    const token =
                      typeof window !== 'undefined'
                        ? localStorage.getItem('accessToken') || ''
                        : '';
                    return `${apiOrigin}/admin/queues/auth?token=${encodeURIComponent(token)}`;
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                  role="menuitem"
                  onClick={() => setShowSettingsMenu(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '10px 14px',
                    color: 'var(--vz-body-color, #1f2937)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--vz-light, #f3f4f6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <LayoutDashboard
                    className="shrink-0"
                    style={{
                      width: 18,
                      height: 18,
                      marginTop: 2,
                      color: 'var(--vz-primary, #468AD4)',
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{t('components.header.queueManager')}</span>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--vz-body-secondary-color, #6b7280)',
                      }}
                    >
                      {t('components.header.queueManagerDesc')}
                    </span>
                  </div>
                </a>
                <a
                  data-testid="header-settings-menu-errors"
                  // #1683 — Errsole "Error Monitor" is mounted on Express at
                  // /admin/errors (not React Router). Same bootstrap as the
                  // Queue Manager: hit the API-side /auth endpoint which verifies
                  // the JWT, sets an HttpOnly cookie scoped to /admin/errors, and
                  // redirects to the dashboard.
                  href={(() => {
                    const apiBase =
                      (import.meta as unknown as Record<string, Record<string, string>>).env
                        ?.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
                    const apiOrigin = apiBase.replace(/\/api\/v1\/?$/, '');
                    const token =
                      typeof window !== 'undefined'
                        ? localStorage.getItem('accessToken') || ''
                        : '';
                    return `${apiOrigin}/admin/errors/auth?token=${encodeURIComponent(token)}`;
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                  role="menuitem"
                  onClick={() => setShowSettingsMenu(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '10px 14px',
                    color: 'var(--vz-body-color, #1f2937)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--vz-light, #f3f4f6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <AlertTriangle
                    className="shrink-0"
                    style={{
                      width: 18,
                      height: 18,
                      marginTop: 2,
                      color: 'var(--vz-primary, #468AD4)',
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{t('components.header.errorMonitor')}</span>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--vz-body-secondary-color, #6b7280)',
                      }}
                    >
                      {t('components.header.errorMonitorDesc')}
                    </span>
                  </div>
                </a>

                {/* Switch User: open the admin impersonation page where a
                    super-admin can sign in as any user in the org. */}
                <button
                  type="button"
                  data-testid="header-settings-menu-switch-user"
                  role="menuitem"
                  onClick={() => { setShowSettingsMenu(false); navigate('/admin/switch-user'); }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '10px 14px',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    color: 'var(--vz-body-color, #1f2937)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--vz-light, #f3f4f6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <UserCog
                    className="shrink-0"
                    style={{ width: 18, height: 18, marginTop: 2, color: 'var(--vz-primary, #468AD4)' }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{t('components.header.switchUser')}</span>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--vz-body-secondary-color, #6b7280)',
                      }}
                    >
                      {t('components.header.switchUserDesc')}
                    </span>
                  </div>
                </button>

                {/* Create User: open the Create User modal where a super-admin
                    can mint a new Super Admin under the active organization. */}
                <button
                  type="button"
                  data-testid="header-settings-menu-create-user"
                  role="menuitem"
                  onClick={() => { setShowSettingsMenu(false); setShowCreateUser(true); }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '10px 14px',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    color: 'var(--vz-body-color, #1f2937)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--vz-light, #f3f4f6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <UserPlus
                    className="shrink-0"
                    style={{ width: 18, height: 18, marginTop: 2, color: 'var(--vz-primary, #468AD4)' }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{t('components.header.createUser')}</span>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--vz-body-secondary-color, #6b7280)',
                      }}
                    >
                      {t('components.header.createUserDesc')}
                    </span>
                  </div>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Icon buttons: notifications, fullscreen */}
        <div ref={notifRef} className="relative">
          <button
            data-platinum-bell
            onClick={() => setShowNotifications(!showNotifications)}
            style={iconBtnStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Bell className="h-[18px] w-[18px]" />
            {totalBadgeCount > 0 && (
              <span
                className="absolute flex items-center justify-center text-white"
                style={{
                  top: '2px',
                  right: '2px',
                  minWidth: '16px',
                  height: '16px',
                  fontSize: '9px',
                  fontWeight: 600,
                  padding: '2px 4px',
                  borderRadius: '800px',
                  backgroundColor: '#f06548',
                  lineHeight: 1,
                }}
              >
                {totalBadgeCount > 99 ? '99+' : totalBadgeCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div
              className="absolute right-0 mt-1 w-80 z-40"
              style={{
                borderRadius: '4.8px',
                boxShadow: 'var(--vz-box-shadow-lg)',
                padding: '8px 0',
                top: '100%',
                backgroundColor: 'var(--vz-card-bg)',
              }}
            >
              <div
                className="flex items-center justify-between px-4 py-3"
                style={{ borderBottom: '1px solid var(--vz-border-color)' }}
              >
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: 500,
                    color: 'var(--vz-heading-color)',
                  }}
                >
                  {t('components.header.notifications')}
                </h3>
                {(unreadCount ?? 0) > 0 && (
                  <button
                    onClick={() => markAllRead.mutate({})}
                    style={{
                      fontSize: '13.2px',
                      color: '#468AD4',
                      fontWeight: 500,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    className="hover:underline"
                  >
                    {t('components.header.markAllRead')}
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {/* Support Tickets section (real, gold/platinum styled). */}
                {supportNotifs.length > 0 && (
                  <>
                    <div
                      className="px-4 py-2 flex items-center gap-2"
                      style={{
                        backgroundColor: 'rgba(212, 175, 55, 0.08)',
                        borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
                      }}
                    >
                      <LifeBuoy className="h-3.5 w-3.5" style={{ color: '#d4af37' }} />
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: '#d4af37',
                        }}
                      >
                        {t('components.header.supportTickets')}
                      </span>
                      {supportUnread > 0 && (
                        <span
                          className="ml-auto rounded-full px-1.5 py-0.5 text-white"
                          style={{
                            fontSize: '9px',
                            fontWeight: 600,
                            backgroundColor: '#d4af37',
                          }}
                        >
                          {supportUnread}
                        </span>
                      )}
                    </div>
                    {supportNotifs.map((notif) => (
                      <div
                        key={notif.id}
                        className="px-4 py-3 cursor-pointer transition-colors duration-150"
                        style={{
                          borderBottom: '1px solid var(--vz-border-color)',
                          borderLeft: notif.read ? 'none' : '3px solid #d4af37',
                          backgroundColor: !notif.read
                            ? 'rgba(212, 175, 55, 0.04)'
                            : 'transparent',
                        }}
                        onClick={() => {
                          // #1535 — resolve a deep-link from entityType+entityId
                          // when possible (falls back to actionUrl, then /support).
                          if (!notif.read) markRead.mutate(notif.id);
                          const target =
                            resolveNotificationRoute(
                              (notif as any).entityType ?? null,
                              (notif as any).entityId ?? null,
                            ) ??
                            (notif as any).actionUrl ??
                            '/support';
                          navigate(target);
                          setShowNotifications(false);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--vz-body-bg)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = !notif.read
                            ? 'rgba(212, 175, 55, 0.04)'
                            : 'transparent';
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <p
                              style={{
                                fontSize: '13.2px',
                                fontWeight: 500,
                                color: '#d4af37',
                              }}
                            >
                              {notif.title}
                            </p>
                            <p
                              style={{
                                fontSize: '12px',
                                color: '#7A8A9A',
                                marginTop: '2px',
                              }}
                              className="line-clamp-2"
                            >
                              {notif.message}
                            </p>
                            <p
                              style={{
                                fontSize: '10px',
                                color: '#adb5bd',
                                marginTop: '4px',
                              }}
                            >
                              {new Date(notif.createdAt).toLocaleString()}
                            </p>
                          </div>
                          {!notif.read && (
                            <div
                              className="mt-1.5 flex-shrink-0 rounded-full"
                              style={{
                                width: '8px',
                                height: '8px',
                                backgroundColor: '#d4af37',
                              }}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {otherNotifs.length > 0 &&
                  otherNotifs.map((notif) => (
                    <div
                      key={notif.id}
                      className="px-4 py-3 cursor-pointer transition-colors duration-150"
                      style={{
                        borderBottom: '1px solid var(--vz-border-color)',
                        backgroundColor: !notif.read
                          ? 'var(--vz-primary-bg-subtle)'
                          : 'transparent',
                      }}
                      onClick={() => {
                        if (!notif.read) markRead.mutate(notif.id);
                        // #1535 — fall through to the entity-route map when
                        // actionUrl isn't set so the bell mirrors the page.
                        const target =
                          notif.actionUrl ??
                          resolveNotificationRoute(
                            (notif as any).entityType ?? null,
                            (notif as any).entityId ?? null,
                          );
                        if (target) {
                          navigate(target);
                          setShowNotifications(false);
                        } else if (notificationsCenterOn) {
                          navigate('/notifications');
                          setShowNotifications(false);
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--vz-body-bg)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = notif.read
                          ? 'transparent'
                          : 'var(--vz-primary-bg-subtle)';
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <p
                            style={{
                              fontSize: '13.2px',
                              fontWeight: 500,
                              color: 'var(--vz-body-color)',
                            }}
                          >
                            {notif.title}
                          </p>
                          <p
                            style={{
                              fontSize: '12px',
                              color: '#7A8A9A',
                              marginTop: '2px',
                            }}
                            className="line-clamp-2"
                          >
                            {notif.message}
                          </p>
                          <p
                            style={{
                              fontSize: '10px',
                              color: '#adb5bd',
                              marginTop: '4px',
                            }}
                          >
                            {new Date(notif.createdAt).toLocaleString()}
                          </p>
                        </div>
                        {!notif.read && (
                          <div
                            className="mt-1.5 flex-shrink-0 rounded-full"
                            style={{
                              width: '8px',
                              height: '8px',
                              backgroundColor: '#468AD4',
                            }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                {/* Empty state — only when nothing is shown above. */}
                {supportNotifs.length === 0 &&
                  otherNotifs.length === 0 && (
                    <div className="px-4 py-8 text-center">
                      <Check
                        className="h-6 w-6 mx-auto mb-2"
                        style={{ color: '#adb5bd' }}
                      />
                      <p style={{ fontSize: '13.2px', color: '#878a99' }}>
                        {t('components.header.noNotifications')}
                      </p>
                    </div>
                  )}
              </div>
              {/* #1535 — "View all notifications" footer (gated on
                  notifications_center). Navigates to the dedicated page. */}
              {notificationsCenterOn && (
                <div
                  className="px-4 py-2 text-center"
                  style={{ borderTop: '1px solid var(--vz-border-color)' }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/notifications');
                      setShowNotifications(false);
                    }}
                    style={{
                      fontSize: '13.2px',
                      color: '#468AD4',
                      fontWeight: 500,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    className="hover:underline"
                  >
                    {t('components.header.viewAllNotifications')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>


        <button
          style={iconBtnStyle}
          title={t('components.header.fullscreen')}
          onClick={handleFullscreen}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <Maximize className="h-[18px] w-[18px]" />
        </button>

        {/* Exit impersonation — shown whenever an app-level Switch User
            session is active, regardless of the impersonated user's role
            (so it survives the role-gated header). Positioned before the
            Onboard Customer button. */}
        {impersonatorSession && (
          <button
            type="button"
            data-testid="header-exit-impersonation-btn"
            title={
              impersonatorSession.viewingAs?.name
                ? t('components.header.viewingAsTitle', { name: impersonatorSession.viewingAs.name })
                : t('components.header.exitImpersonation')
            }
            onClick={() => exitImpersonation()}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(237,94,94,0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(237,94,94,0.2)';
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              height: '32px',
              padding: '0 12px',
              marginLeft: '8px',
              borderRadius: '4px',
              background: 'rgba(237,94,94,0.2)',
              color: 'rgba(255,255,255,0.95)',
              fontSize: '12.5px',
              fontWeight: 500,
              border: '1px solid rgba(237,94,94,0.5)',
              cursor: 'pointer',
              transition: 'background 0.15s',
            }}
          >
            <ArrowLeftRight className="h-4 w-4" />
            <span className="hidden md:inline">
              {t('components.header.exit', { name: impersonatorSession.viewingAs?.name || t('components.header.impersonation') })}
            </span>
          </button>
        )}

        {/* WAYP-491 — Onboard Customer (super admin only) */}
        {isSuperAdmin && (
          <>
            <div
              aria-hidden="true"
              style={{
                width: '1px',
                height: '20px',
                background: 'rgba(255,255,255,0.15)',
                margin: '0 4px',
              }}
            />
            <button
              type="button"
              data-testid="header-onboard-btn"
              title={t('admin.onboardCustomer.headerButton')}
              onClick={() => navigate('/admin/onboard-customer')}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                height: '32px',
                padding: '0 12px',
                marginLeft: '8px',
                borderRadius: '4px',
                background: 'rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.95)',
                fontSize: '12.5px',
                fontWeight: 500,
                border: '1px solid rgba(255,255,255,0.15)',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
            >
              <UserRoundPlus className="h-4 w-4" />
              <span className="hidden md:inline">
                {t('admin.onboardCustomer.headerButton')}
              </span>
            </button>
          </>
        )}

      </div>
    </header>

    {/* Create User (Settings → Create User). Portals to document.body, so it
        renders correctly regardless of the header's stacking context. */}
    <CreateUserModal
      open={showCreateUser}
      onClose={() => setShowCreateUser(false)}
    />
    </>
  );
}
